import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, MapPin, ShieldCheck, ArrowDown } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { AppButtons } from "@/components/Bits";
import { HERO_IMG, CITIES } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} className="relative grain overflow-hidden pt-28 sm:pt-32 lg:pt-36 pb-16" data-testid="hero-section">
      {/* backdrop glows */}
      <div className="pointer-events-none hidden sm:block absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-azo-primary/20 blur-[120px]" />
      <div className="pointer-events-none hidden sm:block absolute top-20 right-0 h-[420px] w-[420px] rounded-full bg-azo-accent/20 blur-[130px]" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-8 items-center">
        {/* Left copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-azo-border glass px-4 py-1.5 text-xs sm:text-sm font-medium"
            data-testid="hero-live-badge"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-azo-emerald animate-pulse-ring" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-azo-emerald" />
            </span>
            300+ verified partners live across 8 Bihar cities
          </motion.div>

          <h1 className="mt-6 font-heading font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-[4.6rem]">
            <MaskedLines
              lines={["Ghar ki har service,", <>ab bas <span className="text-gradient">ek tap</span></>, "door."]}
              start={0.15}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-6 max-w-xl text-base sm:text-lg text-azo-muted leading-relaxed"
          >
            Electrician, Plumber, Carpenter, AC Repair, Cleaning, Barber & Beauty —
            Bihar ke trusted professionals, seedhe aapke doorstep par. Book karo sirf app se.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          >
            <AppButtons className="mt-8" customerTestid="btn-download-customer-app-hero" partnerTestid="btn-download-partner-app-hero" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-azo-muted"
          >
            <span className="inline-flex items-center gap-1.5 font-semibold text-azo-text">
              <Star size={15} className="fill-azo-amber text-azo-amber" /> 4.8★ on Google Play
            </span>
            <span className="hidden sm:inline text-azo-border">•</span>
            <span>Free download • No hidden charge</span>
          </motion.div>

          {/* trust row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3"
          >
            {[
              { icon: Star, label: "4.8★ average rating" },
              { icon: ShieldCheck, label: "Background-verified pros" },
              { icon: MapPin, label: "30-min avg arrival" },
            ].map((t) => (
              <div key={t.label} className="flex items-center gap-2 text-sm text-azo-muted">
                <t.icon size={16} className="text-azo-primary" /> {t.label}
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right visual */}
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: EASE }}
            className="relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden border border-azo-border shadow-azo aspect-[4/5] sm:aspect-[5/5] bg-azo-surface2">
              <motion.img
                src={HERO_IMG}
                alt="AzoApp verified service professional"
                onLoad={() => setHeroLoaded(true)}
                ref={(n) => { if (n && n.complete) setHeroLoaded(true); }}
                style={{ y: imgY, scale: imgScale, willChange: "transform" }}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${heroLoaded ? "opacity-100" : "opacity-0"}`}
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-azo-primary/40 via-transparent to-transparent" />
              <div className="absolute left-4 top-4 rounded-full glass border border-white/20 px-3 py-1.5 text-xs font-mono2 text-white">
                AZOAPP · VERIFIED PRO
              </div>
            </div>

            {/* floating booking card */}
            <motion.div
              style={{ y: cardY, willChange: "transform" }}
              className="absolute -left-3 sm:-left-8 bottom-8 w-52 sm:w-64 rounded-2xl bg-azo-surface border border-azo-border shadow-azo p-4 animate-float"
              data-testid="hero-booking-card"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono2 text-azo-muted uppercase tracking-widest">Live booking</span>
                <span className="h-2 w-2 rounded-full bg-azo-emerald" />
              </div>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-10 w-10 grid place-items-center rounded-xl bg-azo-primary/12 text-azo-primary text-lg">⚡</div>
                <div className="leading-tight">
                  <p className="text-sm font-semibold font-heading">Electrician</p>
                  <p className="text-xs text-azo-muted">Patna · arriving in 22 min</p>
                </div>
              </div>
              <div className="mt-3 h-1.5 w-full rounded-full bg-azo-surface2 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-azo-primary"
                  initial={{ width: "10%" }}
                  animate={{ width: "78%" }}
                  transition={{ duration: 2.4, delay: 1.4, ease: EASE }}
                />
              </div>
            </motion.div>

            {/* rating chip */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2, duration: 0.6, ease: EASE }}
              className="absolute -right-2 sm:-right-6 top-10 rounded-2xl bg-azo-primary text-white px-4 py-3 shadow-azo"
            >
              <p className="text-2xl font-heading font-extrabold leading-none">4.8★</p>
              <p className="text-[10px] uppercase tracking-widest opacity-80 font-mono2 mt-1">12k+ reviews</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* city selector pills */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.1 }}
        className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 mt-12"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-mono2 uppercase tracking-widest text-azo-muted mr-1">Now serving</span>
          {CITIES.map((c) => (
            <a
              key={c.name}
              href="#cities"
              data-testid={`hero-city-pill-${c.name.toLowerCase()}`}
              className="rounded-full border border-azo-border px-3.5 py-1.5 text-sm hover:bg-azo-primary hover:text-white hover:border-azo-primary transition-all duration-300"
            >
              {c.name}
            </a>
          ))}
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 mt-10 flex items-center gap-2 text-xs text-azo-muted font-mono2">
        <ArrowDown size={14} className="animate-bounce" /> scroll to explore
      </div>
    </section>
  );
}
